import { ShapeTextView } from './text/shape-text-view';
import { ShapeTextModel } from './../model/text/shape-text.mdl';
import { Bitmap, Graphics, Shape } from '@creately/createjs-module';
import { ImageRenderView, ITextView } from 'flux-diagram-composer';
import { ShapeModel } from '../model/shape.mdl';
import { ShapeDisplayView } from './shape-display-view';
import { AppConfig, Logger } from 'flux-core';
import { ShapeType } from 'flux-definition';
import { TiptapShapeTextView } from './text/tiptap-shape-text-view';
import { fromPromise } from 'rxjs/internal-compatibility';

/**
 * The image view that will be used for canvas rendering.
 * This is the primary view that will be used on nucleus
 * to visually represent shapes based on custom images. This
 * adeheres to the rendering requirements of Diagram Composer
 * platform by extending the ImageRenderView.
 *
 * This shape will use EaselJS along with its parent DiagramView.
 * The functionality on this class will be dominated by the capabilities
 * of EaselJS.
 *
 * Unlike other views, image view does not draw uding the graphics of an
 * EaselJS Shape.. Instead the image is drawn using a Bitmap and added to
 * the container.
 *
 * @author  Ramishka
 * @since   2019-02-20
 */
export class ImageView extends ImageRenderView {

    /**
     * This is the easeljs view of the shape that will contain
     * all the peices that show the shape. Every view part of the shape
     * must be added into this container.
     */
    protected container: ShapeDisplayView;

    /**
     * The easeljs representation of the drawing space. Everything
     * will be drawn on this Bitmap object
     */
    protected image: Bitmap;

    protected imageBorder: Shape;

    /**
     * The HTMLImageElement that is used to load the image.
     * The image is first loaded via the Image element and then
     * added to the Bitmap to get around CORS and tainted canvas
     * issues.
     */
    protected imageElem: HTMLImageElement;

    /**
     * The model associated with this shape.
     * @override
     */
    public get model(): ShapeModel {
        return <ShapeModel>this._model;
    }

    /**
     * Expected output of a shape.
     * @override
     */
    public get renderedShape(): ShapeDisplayView {
        return this.container;
    }

    /**
     * Expected IGraphics instance attached to this shape
     * @override
     */
    public get graphics(): Graphics {
        return this.imageBorder.graphics;
    }

    /**
     * Called when the shape is added.
     * @override
     */
    public initialize(): Promise<any> {
        this.container = this.getContainer( this.id, this.model.type );
        this.imageElem = this.getImageElement();
        this.imageElem.crossOrigin = 'anonymous';
        this.imageElem.onload = () => this.onImageLoad();
        this.imageElem.onerror = error => this.onImageLoadFailure( error );
        this.imageElem.src = this._imageSource;
        this.image = this.getBitmap( this.imageElem );
        this.imageBorder = new Shape();
        this.container.addChild( this.imageBorder );
        this.container.addChild( this.image );
        return this.retry( this.model, 10, 2 );
    }
    /**
     * This method ultimately redraws the bitmap on the given
     * drawing space. The method would ensure that all data changes
     * are translated into the drawing space as intended by this shape.
     * This means the full shape will be up-to-date on the drawing space
     * when this method completes until the next data change on the model.
     * @param change A json with all the changes that happened to the model.
     *          Can be used to check which props changed or what changes happened.
     */
     public render( change?: any ) {
        // NOTE: If the rendering method is switched,
        // the text view should be recreated, so it's removed here so that it will be recreated
        if (  change?.$set ) {
            const regex = /texts\.([^\.]+)\.rendering$/;
            Object.keys( change.$set || {})
                .filter( k => regex.test( k ))
                .forEach( k => {
                    const textId = k.split( '.' )[ 1 ];
                    const textView = this._texts.find( tv => tv.name === textId );
                    this.removeText( textView );
                });
            if ( change.$set.hashScaled ) {
                fromPromise( this.updateImageSrc( change.$set.hashScaled )).subscribe();
            }
        }
        super.render( change );
        const width = this.model.drawWidth - this.model.style.lineThickness;
        const height = this.model.drawHeight - this.model.style.lineThickness;
        this.scaleWidth( width / this.imageElem.width );
        this.scaleHeight( height / this.imageElem.height );
        // NOTE: If we use the model default bounds to set the image bounds
        // then the gluepoints will not be shown properly. This is due to the
        // inaccurate calculation will be done by collision detector when the
        // mouse hover over the image with the connector end point to trigger
        // the gluepoints.
        this.image.setBounds( 0, 0, this.imageElem.width, this.imageElem.height );
    }

    protected updateImageSrc( hash: string, params = '' ): Promise<boolean> {
        const src = AppConfig.get( 'CUSTOM_IMAGE_BASE_URL' ) + hash + params;
        return new Promise( resolve => {
            this.imageElem.onload = () => resolve( true );
            this.imageElem.onerror = () => resolve( false );
            this.imageElem.src = src;
        });
    }

    /**
     * This method is called when the image load completes.
     * The shape is rendered and the view is updated once
     * to reflect the changes immediately.
     */
    protected onImageLoad() {
        this.render();
        if ( this.container && this.container.stage ) {
            this.container.stage.update();
        } else {
            Logger.warning( 'Image on loading failed due to unknown error. Either container or stage is null.' );
        }
    }

    /**
     * This method is called when the image loading has failed.
     * @param error - image error
     */
    protected onImageLoadFailure( error ) {
        Logger.error( `Image loading failed for shape ${this.model.id}` );
    }

    /**
     * This function creates a text view from the given
     * text model and adds it to the container and text views array
     * @param ShapeTextModel
     * @override
     */
    protected addText( text: ShapeTextModel ) {
        if ( text.rendering === 'tiptapCanvas' ) {
            const textView = new TiptapShapeTextView( text, this.model.id );
            this.container.addChild( textView );
            this._texts.push( textView );
        } else {
            const textView = new ShapeTextView( text );
            this.container.addChild( textView );
            this._texts.push( textView );
        }
    }

    /**
     * This removes the text view from the text view list. This also
     * must ensure it is removed from the rendering.
     * @param text The text view to remove.
     */
    protected removeText( text: ITextView ) {
        super.removeText( text );
        this.renderedShape.removeChild( text as any );
    }


    /**
     * Moves the image to the given location
     * @param x the position on the canvas to move to
     * @param y the position on the canvas to move to
     * @override
     */
    protected move( x: number, y: number ) {
        this.image.x = x;
        this.image.y = y;

        this.imageBorder.x = x;
        this.imageBorder.y = y;
    }

    /**
     * Scales the image in height.
     * @param scale The scale multiplier
     */
    protected scaleHeight( scale: number ) {
        this.image.scaleY = scale;
        this.image.regY = ( - this.model.style.lineThickness / 2 ) / scale;
    }

    /**
     * Scales the image in width.
     * @param scale The scale multiplier
     */
    protected scaleWidth( scale: number ) {
        this.image.scaleX = scale;
        this.image.regX = ( - this.model.style.lineThickness / 2 ) / scale;
    }

    /**
     * Rotates the image to the given angle
     * @param angle The angle to rotate to.
     */
    protected rotate( angle: number ) {
        this.image.rotation = angle;
        this.imageBorder.rotation = angle;
    }

    /**
     * Retrieves a new Image instance
     */
    private getImageElement(): HTMLImageElement {
        return new Image();
    }

    /**
     * Retrieves a  new Bitmap instance
     * @param image - image to base the bitmap on
     */
    private getBitmap( image: HTMLImageElement ): Bitmap {
        return new Bitmap( this.imageElem );
    }

    /**
     * Returns an instance of the shape display view
     * @param id - container id
     * @param type - shape type
     */
    private getContainer( id: string, type: ShapeType ): ShapeDisplayView {
        return new ShapeDisplayView( id, type );
    }
}

